// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
} from "@insite/client-framework/HandlerCreator";
import { getProductById, GetProductByIdApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getProductState, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    productId?: string;
    skipInventoryValidation?: boolean;
}

interface Props {
    apiParameter: GetProductByIdApiV2Parameter;
    product: ProductModel;
    productInfo: ProductInfo;
    pricingLoaded?: boolean;
}

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginSetProduct: HandlerType = (props: any) => {
    props.dispatch({
        type: "QuickView/BeginSetProduct",
        id: props.parameter.productId,
    });
};

export const PopulateApiParameter: HandlerType = (props: any) => {
    if (props.parameter.productId) {
        props.apiParameter = {
            id: props.parameter.productId,
            expand: ["detail", "images", "properties"],
        };
    }
};

export const RequestDataFromApi: HandlerType = async (props: any) => {
    const { apiParameter } = props;
    if (!apiParameter) {
        return;
    }

    // shoud always load the product since we need proerpties and other data
    if (apiParameter) {
        props.product = await executeAwaitableHandlerChain(loadProduct, props.apiParameter, props);
    }
};

export const SetProductData: HandlerType = (props: any) => {
    if (props.product) {
        const product = props.product;
        props.dispatch({
            type: "QuickView/SetProduct",
            product,
        });
        props.productInfo = createFromProduct(props.product);
    }
};

export const LoadPricing: HandlerType = async props => {
    let pricingLoaded = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: [props.productInfo],
            onSuccess: realTimePricing => {
                if (realTimePricing) {
                    const pricing = realTimePricing.realTimePricingResults?.find(
                        o => o.productId === props.product.id,
                    );
                    props.productInfo = { ...props.productInfo, pricing };
                }
                pricingLoaded = true;
            },
            onError: () => {
                pricingLoaded = true;
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => pricingLoaded);
    }
};

export const LoadInventory: HandlerType = async props => {
    const product = props.product;
    if (!product) {
        return;
    }
    props.productInfo = createFromProduct(product);

    let loadedInventory = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds: [product.id],
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    if (props.productInfo) {
                        props.productInfo.failedToLoadInventory = true;
                    }
                } else if (props.productInfo) {
                    props.productInfo.inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                        o => o.productId === product.id,
                    );
                }
                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const SetProductInfo: HandlerType = (props: any) => {
    if (props.productInfo) {
        const productInfo = props.productInfo;
        props.dispatch({
            type: "QuickView/SetProductInfo",
            productInfo,
        });
    }
};

export const DispatchCompleteSetProduct: HandlerType = (props: any) => {
    props.dispatch({
        type: "QuickView/CompleteSetProduct",
    });
};

export const chain = [
    DispatchBeginSetProduct,
    PopulateApiParameter,
    RequestDataFromApi,
    SetProductData,
    LoadPricing,
    LoadInventory,
    SetProductInfo,
    DispatchCompleteSetProduct,
];

const setProduct = createHandlerChainRunner(chain, "SetProduct");
export default setProduct;
